'use client';

import * as React from 'react';

import { Select as SelectPrimitive } from '@base-ui/react/select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';

import { withBaseClasses } from '@/utils/cn';

const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<HTMLButtonElement, SelectPrimitive.Trigger.Props>(
  ({ className, children, ...props }, ref) => (
    <SelectPrimitive.Trigger
      className={withBaseClasses(
        [
          'flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-black/10 bg-transparent px-3 py-2 font-medium text-black text-sm shadow-sm data-[placeholder]:text-black/50 focus:outline-none focus:ring-1 focus:ring-black/20 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
        ],
        className,
      )}
      ref={ref}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon render={<ChevronDown className="h-4 w-4 opacity-50" />} />
    </SelectPrimitive.Trigger>
  ),
);
SelectTrigger.displayName = 'SelectTrigger';

const SelectScrollUpButton = React.forwardRef<HTMLDivElement, SelectPrimitive.ScrollUpArrow.Props>(
  ({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollUpArrow
      className={withBaseClasses(
        ['top-0 z-10 flex w-full cursor-default items-center justify-center bg-white py-1'],
        className,
      )}
      ref={ref}
      {...props}
    >
      <ChevronUp className="h-4 w-4" />
    </SelectPrimitive.ScrollUpArrow>
  ),
);
SelectScrollUpButton.displayName = 'SelectScrollUpButton';

const SelectScrollDownButton = React.forwardRef<HTMLDivElement, SelectPrimitive.ScrollDownArrow.Props>(
  ({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollDownArrow
      className={withBaseClasses(
        ['bottom-0 z-10 flex w-full cursor-default items-center justify-center bg-white py-1'],
        className,
      )}
      ref={ref}
      {...props}
    >
      <ChevronDown className="h-4 w-4" />
    </SelectPrimitive.ScrollDownArrow>
  ),
);
SelectScrollDownButton.displayName = 'SelectScrollDownButton';

type SelectContentProps = SelectPrimitive.Popup.Props &
  Pick<
    SelectPrimitive.Positioner.Props,
    'side' | 'sideOffset' | 'align' | 'alignOffset' | 'alignItemWithTrigger' | 'collisionPadding' | 'collisionAvoidance'
  >;

const SelectContent = React.forwardRef<HTMLDivElement, SelectContentProps>(
  (
    {
      className,
      children,
      side = 'bottom',
      sideOffset = 4,
      align = 'start',
      alignOffset = 0,
      alignItemWithTrigger = false,
      collisionPadding = 10,
      collisionAvoidance = { align: 'shift', fallbackAxisSide: 'none', side: 'flip' },
      ...props
    },
    ref,
  ) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        align={align}
        alignItemWithTrigger={alignItemWithTrigger}
        alignOffset={alignOffset}
        className="z-50"
        collisionAvoidance={collisionAvoidance}
        collisionPadding={collisionPadding}
        side={side}
        sideOffset={sideOffset}
      >
        <SelectPrimitive.Popup
          className={withBaseClasses(
            [
              'relative z-50 max-h-[var(--available-height)] min-w-[8rem] overflow-x-hidden overflow-y-auto rounded-md border border-black/10 bg-white text-black shadow-lg data-[open]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[open]:fade-in-0 data-[closed]:zoom-out-95 data-[open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[var(--transform-origin)]',
            ],
            className,
          )}
          ref={ref}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List className="max-h-[var(--available-height)] w-full min-w-[var(--anchor-width)] overflow-y-auto p-1">
            {children}
          </SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  ),
);
SelectContent.displayName = 'SelectContent';

const SelectLabel = React.forwardRef<HTMLDivElement, SelectPrimitive.GroupLabel.Props>(
  ({ className, ...props }, ref) => (
    <SelectPrimitive.GroupLabel
      className={withBaseClasses(['px-2 py-1.5 text-sm font-semibold'], className)}
      ref={ref}
      {...props}
    />
  ),
);
SelectLabel.displayName = 'SelectLabel';

const SelectItem = React.forwardRef<HTMLElement, SelectPrimitive.Item.Props>(
  ({ className, children, ...props }, ref) => (
    <SelectPrimitive.Item
      className={withBaseClasses(
        [
          'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 font-medium text-black text-sm outline-none focus:outline-2 focus:outline-solid focus:outline-current focus:outline-offset-[0.2em] focus:bg-black/5 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-black/5',
        ],
        className,
      )}
      ref={ref}
      {...props}
    >
      <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  ),
);
SelectItem.displayName = 'SelectItem';

const SelectSeparator = React.forwardRef<HTMLDivElement, SelectPrimitive.Separator.Props>(
  ({ className, ...props }, ref) => (
    <SelectPrimitive.Separator
      className={withBaseClasses(['-mx-1 my-1 h-px bg-black/10'], className)}
      ref={ref}
      {...props}
    />
  ),
);
SelectSeparator.displayName = 'SelectSeparator';

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
